/**
 * The patterns every module reuses (docs/UI-STANDARDS.md): EditableBlock,
 * DataList, ReferenceListEditor, SettingsForm, PermissionAction,
 * ImpactPreview, RecordHistory. Built once here, not per module.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { Icon, type IconName } from "./Icon.tsx";
import { Alert, Badge, Button, EmptyState, Input, Select, SkeletonRows, Switch } from "./primitives.tsx";
import { errorMessage, useToast } from "./feedback.tsx";
import { useDelayed, useReportDirty, formatDateTime, relativeTime } from "./hooks.ts";

/* ------------------------------------------------------------------ */
/* EditableBlock — read view → Edit → Save / Cancel, dirty guard        */
/* ------------------------------------------------------------------ */
export interface EditableBlockProps<T extends object> {
  id: string;
  title: ReactNode;
  value: T;
  /** Read-only rendering. */
  view: (value: T) => ReactNode;
  /** Form rendering while editing. */
  form: (draft: T, set: (patch: Partial<T>) => void, errors: Record<string, string>) => ReactNode;
  /** Return field errors; empty = valid. Runs on every change (save disabled until valid). */
  validate?: (draft: T) => Record<string, string | undefined>;
  onSave: (draft: T, original: T) => Promise<unknown>;
  canEdit: boolean;
  /** Why the user may not edit — shown on the disabled Edit button. */
  reason?: string;
  savedToast?: string;
  /** Extra actions in the header (read mode only). */
  actions?: ReactNode;
  /** Called with a 409 stale error so the caller can refetch. */
  onStale?: (details: unknown) => void;
}

export function EditableBlock<T extends object>({ id, title, value, view, form, validate, onSave, canEdit, reason, savedToast, actions, onStale }: EditableBlockProps<T>) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<T>(value);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string>>({});
  const toast = useToast();
  const original = useRef(value);

  useEffect(() => {
    if (!editing) {
      setDraft(value);
      original.current = value;
    }
  }, [value, editing]);

  const dirty = editing && JSON.stringify(draft) !== JSON.stringify(original.current);
  useReportDirty(id, dirty);
  const errors = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(validate ? validate(draft) : {})) if (v) out[k] = v;
    return { ...out, ...serverFieldErrors };
  }, [draft, validate, serverFieldErrors]);
  const valid = Object.keys(errors).length === 0;

  const set = useCallback((patch: Partial<T>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setServerFieldErrors({});
  }, []);

  const cancel = () => {
    setDraft(original.current);
    setEditing(false);
    setServerError(null);
    setServerFieldErrors({});
  };

  const save = async () => {
    if (!dirty || !valid) return;
    setSaving(true);
    setServerError(null);
    try {
      await onSave(draft, original.current);
      toast.success(savedToast ?? `${typeof title === "string" ? title : "Changes"} saved`);
      setEditing(false);
    } catch (e) {
      const err = e as { error?: string; details?: unknown };
      if (err?.error === "stale") {
        setServerError("Someone else changed this record since you opened it. Review their changes before saving again.");
        onStale?.(err.details);
      } else if (err?.error === "validation" && Array.isArray(err.details)) {
        const fe: Record<string, string> = {};
        for (const d of err.details as Array<{ path: string; message: string }>) fe[d.path] = d.message;
        setServerFieldErrors(fe);
        setServerError("Some fields need attention.");
      } else setServerError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`editable-block${editing ? " editing" : ""}`} aria-labelledby={`${id}-title`}>
      <div className="eb-head">
        <h3 id={`${id}-title`}>{title}</h3>
        <div className="eb-actions">
          {!editing && actions}
          {!editing && (
            <PermissionAction allowed={canEdit} reason={reason}>
              <Button size="sm" icon="edit" onClick={() => setEditing(true)}>
                Edit
              </Button>
            </PermissionAction>
          )}
          {editing && (
            <>
              <Button size="sm" variant="ghost" onClick={cancel} disabled={saving}>
                Cancel
              </Button>
              <Button size="sm" variant="primary" onClick={save} disabled={!dirty || !valid} loading={saving} title={!dirty ? "Nothing has changed" : !valid ? "Fix the highlighted fields" : undefined}>
                Save
              </Button>
            </>
          )}
        </div>
      </div>
      {serverError && (
        <Alert tone="danger" className="mb-4">
          {serverError}
        </Alert>
      )}
      {editing ? form(draft, set, errors) : view(value)}
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* PermissionAction — hide what you may not see; disable-with-reason    */
/* ------------------------------------------------------------------ */
export function PermissionAction({ allowed, reason, hidden, children }: { allowed: boolean; reason?: string; hidden?: boolean; children: ReactNode }) {
  if (hidden) return null;
  if (allowed) return <>{children}</>;
  return (
    <span title={reason ?? "You do not have permission for this"} style={{ display: "inline-flex", cursor: "not-allowed" }} aria-disabled="true">
      <span style={{ pointerEvents: "none", opacity: 0.5, display: "inline-flex" }}>{children}</span>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* DataList — filter bar in the URL, keyset paging, five states         */
/* ------------------------------------------------------------------ */
export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  num?: boolean;
  width?: number | string;
  sortable?: boolean;
  className?: string;
}
export interface DataListProps<T> {
  items: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  filtersActive?: boolean;
  onClearFilters?: () => void;
  empty?: { title: ReactNode; text?: ReactNode; action?: ReactNode; icon?: IconName };
  partial?: ReactNode;
  onRowClick?: (row: T) => void;
  rowMenu?: (row: T) => ReactNode;
  selectable?: boolean;
  selected?: Set<string>;
  onSelectedChange?: (s: Set<string>) => void;
  bulkBar?: (selected: Set<string>) => ReactNode;
  sort?: { key: string; dir: "asc" | "desc" } | null;
  onSort?: (key: string) => void;
  footer?: ReactNode;
  rowClass?: (row: T) => string | undefined;
}

export function DataList<T>({ items, columns, rowKey, loading, error, onRetry, filtersActive, onClearFilters, empty, partial, onRowClick, rowMenu, selectable, selected, onSelectedChange, bulkBar, sort, onSort, footer, rowClass }: DataListProps<T>) {
  const showSkeleton = useDelayed(!!loading && items.length === 0, 200);
  const sel = selected ?? new Set<string>();
  const allSelected = selectable && items.length > 0 && items.every((r) => sel.has(rowKey(r)));
  const toggleAll = () => onSelectedChange?.(allSelected ? new Set() : new Set(items.map(rowKey)));
  const toggle = (k: string) => {
    const n = new Set(sel);
    if (n.has(k)) n.delete(k);
    else n.add(k);
    onSelectedChange?.(n);
  };

  if (error && items.length === 0) {
    return <EmptyState error title="Could not load this list" text={errorMessage(error)} actions={onRetry && <Button variant="primary" icon="refresh" onClick={onRetry}>Try again</Button>} />;
  }
  if (loading && items.length === 0) return showSkeleton ? <SkeletonRows cols={Math.min(columns.length, 5)} /> : <div style={{ minHeight: 120 }} />;
  if (!loading && items.length === 0) {
    if (filtersActive) return <EmptyState icon="filter" title="Nothing matches these filters" text="Try widening the search or clearing the filters." actions={onClearFilters && <Button onClick={onClearFilters}>Clear filters</Button>} />;
    return <EmptyState icon={empty?.icon ?? "list"} title={empty?.title ?? "Nothing here yet"} text={empty?.text} actions={empty?.action} />;
  }
  return (
    <>
      {partial && (
        <div className="partial-note">
          <Icon name="clock" size={14} /> {partial}
        </div>
      )}
      {selectable && sel.size > 0 && bulkBar && (
        <div className="bulk-bar">
          <span>{sel.size} selected</span>
          {bulkBar(sel)}
          <button type="button" className="link-cell right" onClick={() => onSelectedChange?.(new Set())}>
            Clear
          </button>
        </div>
      )}
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              {selectable && (
                <th className="checkcol">
                  <input type="checkbox" className="row-check" aria-label="Select all" checked={!!allSelected} onChange={toggleAll} />
                </th>
              )}
              {columns.map((c) => (
                <th key={c.key} scope="col" className={c.num ? "num" : undefined} style={c.width ? { width: c.width } : undefined}>
                  {c.sortable && onSort ? (
                    <button type="button" onClick={() => onSort(c.key)} aria-sort={sort?.key === c.key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}>
                      {c.header}
                      {sort?.key === c.key && <Icon name={sort.dir === "asc" ? "chev-up" : "chev-down"} size={14} />}
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              ))}
              {rowMenu && <th style={{ width: 44 }} />}
            </tr>
          </thead>
          <tbody>
            {items.map((row) => {
              const k = rowKey(row);
              return (
                <tr key={k} className={`${onRowClick ? "clickable " : ""}${rowClass?.(row) ?? ""}`.trim() || undefined} onClick={onRowClick ? () => onRowClick(row) : undefined} tabIndex={onRowClick ? 0 : undefined} onKeyDown={onRowClick ? (e) => e.key === "Enter" && onRowClick(row) : undefined}>
                  {selectable && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" className="row-check" aria-label="Select row" checked={sel.has(k)} onChange={() => toggle(k)} />
                    </td>
                  )}
                  {columns.map((c) => (
                    <td key={c.key} className={`${c.num ? "num " : ""}${c.className ?? ""}`.trim() || undefined}>
                      {c.render(row)}
                    </td>
                  ))}
                  {rowMenu && <td onClick={(e) => e.stopPropagation()}>{rowMenu(row)}</td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {footer}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* ReferenceListEditor — label · key · active · where-used · reorder    */
/* ------------------------------------------------------------------ */
export interface RefValue {
  valueKey: string;
  label: string;
  labelHe?: string | null;
  active: boolean;
  order: number;
  whereUsed?: number;
  updatedAt?: string | null;
  colour?: string | null;
  metadata?: Record<string, unknown>;
}
/** A typed metadata field a list's values carry (mirrors @crc/shared REFERENCE_LIST_FIELDS). */
export interface RefFieldDef {
  key: string;
  label: string;
  type: "number" | "boolean" | "text";
  help?: string;
  min?: number;
  max?: number;
  required?: boolean;
}
export interface ReferenceListEditorProps {
  values: RefValue[];
  canEdit: boolean;
  reason?: string;
  onAdd: (v: { valueKey: string; label: string; labelHe?: string | null; metadata?: Record<string, unknown> }) => Promise<unknown>;
  onUpdate: (valueKey: string, patch: { label?: string; labelHe?: string | null; active?: boolean; metadata?: Record<string, unknown> }) => Promise<unknown>;
  onReorder: (keys: string[]) => Promise<unknown>;
  showHebrew?: boolean;
  onWhereUsed?: (valueKey: string) => void;
  /** typed metadata fields this list's values carry; shown on add and edit, summarised beside the label */
  fields?: RefFieldDef[];
}

function fieldValue(f: RefFieldDef, raw: unknown): string | number | boolean | undefined {
  if (f.type === "boolean") return raw === true;
  if (f.type === "number") { const n = Number(raw); return raw === "" || raw === undefined || raw === null || !Number.isFinite(n) ? undefined : n; }
  return typeof raw === "string" && raw.trim() ? raw.trim() : undefined;
}
function fieldsValid(fields: RefFieldDef[] | undefined, draft: Record<string, unknown>): boolean {
  return (fields ?? []).every((f) => { const v = fieldValue(f, draft[f.key]); if (v === undefined) return !f.required; if (f.type === "number" && typeof v === "number") return (f.min === undefined || v >= f.min) && (f.max === undefined || v <= f.max); return true; });
}
function fieldsOut(fields: RefFieldDef[] | undefined, draft: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields ?? []) { const v = fieldValue(f, draft[f.key]); if (v !== undefined) out[f.key] = v; }
  return out;
}
function FieldInputs({ fields, draft, onChange }: { fields: RefFieldDef[]; draft: Record<string, unknown>; onChange: (d: Record<string, unknown>) => void }) {
  return (
    <>
      {fields.map((f) =>
        f.type === "boolean" ? (
          <Switch key={f.key} checked={draft[f.key] === true} onChange={(on) => onChange({ ...draft, [f.key]: on })} label={f.label} />
        ) : (
          <Input key={f.key} small type={f.type === "number" ? "number" : "text"} min={f.min} max={f.max} placeholder={f.label} title={f.help} aria-label={f.label} value={draft[f.key] === undefined || draft[f.key] === null ? "" : String(draft[f.key])} onChange={(e) => onChange({ ...draft, [f.key]: e.target.value })} style={{ maxWidth: f.type === "number" ? 120 : 200 }} />
        ),
      )}
    </>
  );
}
function fieldSummary(fields: RefFieldDef[] | undefined, metadata: Record<string, unknown> | undefined): string {
  return (fields ?? []).map((f) => { const v = metadata?.[f.key]; if (f.type === "boolean") return `${f.label}: ${v === true ? "yes" : "no"}`; return v === undefined || v === null || v === "" ? `${f.label}: —` : `${f.label}: ${String(v)}`; }).join(" · ");
}

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60);
}

export function ReferenceListEditor({ values, canEdit, reason, onAdd, onUpdate, onReorder, showHebrew, onWhereUsed, fields }: ReferenceListEditorProps) {
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newKey, setNewKey] = useState("");
  const [newHe, setNewHe] = useState("");
  const [newMeta, setNewMeta] = useState<Record<string, unknown>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editHe, setEditHe] = useState("");
  const [editMeta, setEditMeta] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const sorted = useMemo(() => [...values].sort((a, b) => a.order - b.order), [values]);

  const run = async (key: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(key);
    try {
      await fn();
      toast.success(done);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };
  const move = (i: number, dir: -1 | 1) => {
    const keys = sorted.map((v) => v.valueKey);
    const j = i + dir;
    if (j < 0 || j >= keys.length) return;
    [keys[i], keys[j]] = [keys[j]!, keys[i]!];
    void run("reorder", () => onReorder(keys), "Order saved");
  };

  return (
    <div>
      <div className="ref-row head">
        <span />
        <span>Label{showHebrew ? " / עברית" : ""}</span>
        <span>Key</span>
        <span>Active</span>
        <span>Used</span>
        <span />
      </div>
      {sorted.map((v, i) => (
        <div key={v.valueKey} className={`ref-row${v.active ? "" : " inactive"}`}>
          <span className="drag flex" style={{ flexDirection: "column", gap: 0 }}>
            <button type="button" className="icon-btn plain" style={{ height: 16 }} aria-label="Move up" disabled={!canEdit || i === 0} onClick={() => move(i, -1)}>
              <Icon name="chev-up" size={14} />
            </button>
            <button type="button" className="icon-btn plain" style={{ height: 16 }} aria-label="Move down" disabled={!canEdit || i === sorted.length - 1} onClick={() => move(i, 1)}>
              <Icon name="chev-down" size={14} />
            </button>
          </span>
          {editing === v.valueKey ? (
            <span className="flex wrap">
              <Input small value={editLabel} onChange={(e) => setEditLabel(e.target.value)} aria-label="Label" style={{ maxWidth: 220 }} />
              {showHebrew && <Input small className="he" dir="rtl" value={editHe} onChange={(e) => setEditHe(e.target.value)} aria-label="Hebrew label" style={{ maxWidth: 180 }} />}
              {fields && fields.length > 0 && <FieldInputs fields={fields} draft={editMeta} onChange={setEditMeta} />}
              <Button size="xs" variant="primary" loading={busy === v.valueKey} disabled={!editLabel.trim() || !fieldsValid(fields, editMeta)} onClick={() => run(v.valueKey, () => onUpdate(v.valueKey, { label: editLabel.trim(), labelHe: showHebrew ? editHe.trim() || null : undefined, ...(fields && fields.length ? { metadata: { ...(v.metadata ?? {}), ...fieldsOut(fields, editMeta) } } : {}) }), `"${editLabel.trim()}" saved`).then(() => setEditing(null))}>
                Save
              </Button>
              <Button size="xs" variant="ghost" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </span>
          ) : (
            <span>
              <b>{v.label}</b>
              {showHebrew && v.labelHe && <span className="he muted" style={{ marginInlineStart: 8 }}>{v.labelHe}</span>}
              {fields && fields.length > 0 && <div className="small muted">{fieldSummary(fields, v.metadata)}</div>}
              {!v.active && <Badge tone="neutral" className="mt-2" title="Hidden from new records; kept on existing ones">retired</Badge>}
            </span>
          )}
          <span className="mono muted" title="Stable key — never changes after use">{v.valueKey}</span>
          <span>
            <Switch checked={v.active} disabled={!canEdit || busy === v.valueKey} onChange={(on) => run(v.valueKey, () => onUpdate(v.valueKey, { active: on }), on ? `"${v.label}" reactivated` : `"${v.label}" retired — existing records keep it`)} />
          </span>
          <span>
            {v.whereUsed ? (
              <button type="button" className="link-cell" onClick={() => onWhereUsed?.(v.valueKey)} title="Records using this value">
                {v.whereUsed}
              </button>
            ) : (
              <span className="faint">0</span>
            )}
          </span>
          <span className="flex" style={{ justifyContent: "flex-end" }}>
            {editing !== v.valueKey && (
              <PermissionAction allowed={canEdit} reason={reason}>
                <Button size="xs" variant="ghost" icon="edit" onClick={() => { setEditing(v.valueKey); setEditLabel(v.label); setEditHe(v.labelHe ?? ""); setEditMeta({ ...(v.metadata ?? {}) }); }}>
                  Edit
                </Button>
              </PermissionAction>
            )}
          </span>
        </div>
      ))}
      <div style={{ padding: "12px 20px" }}>
        {adding ? (
          <div className="flex wrap">
            <Input small placeholder="Label" value={newLabel} autoFocus onChange={(e) => { setNewLabel(e.target.value); setNewKey(slugify(e.target.value)); }} style={{ maxWidth: 220 }} aria-label="New label" />
            <Input small mono placeholder="key" value={newKey} onChange={(e) => setNewKey(slugify(e.target.value))} style={{ maxWidth: 180 }} aria-label="Stable key" />
            {showHebrew && <Input small className="he" dir="rtl" placeholder="עברית" value={newHe} onChange={(e) => setNewHe(e.target.value)} style={{ maxWidth: 160 }} aria-label="Hebrew label" />}
            {fields && fields.length > 0 && <FieldInputs fields={fields} draft={newMeta} onChange={setNewMeta} />}
            <Button size="sm" variant="primary" loading={busy === "add"} disabled={!newLabel.trim() || !newKey || !fieldsValid(fields, newMeta)} onClick={() => run("add", () => onAdd({ valueKey: newKey, label: newLabel.trim(), labelHe: showHebrew ? newHe.trim() || null : undefined, ...(fields && fields.length ? { metadata: fieldsOut(fields, newMeta) } : {}) }), `"${newLabel.trim()}" added`).then(() => { setAdding(false); setNewLabel(""); setNewKey(""); setNewHe(""); setNewMeta({}); })}>
              Add value
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <PermissionAction allowed={canEdit} reason={reason}>
            <button type="button" className="add-row-btn" style={{ marginTop: 0 }} onClick={() => setAdding(true)}>
              <Icon name="plus" size={14} /> Add value
            </button>
          </PermissionAction>
        )}
        {fields && fields.length > 0 && <p className="muted small mt-3">{fields.map((f) => f.help ? `${f.label}: ${f.help}` : f.label).join(" ")}</p>}
        <p className="muted small mt-3">Values are retired, never deleted — a value in use stays on every record that has it.</p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SettingsForm — defaults beside values, reset, dirty save bar         */
/* ------------------------------------------------------------------ */
export interface SettingItem {
  key: string;
  group: string;
  label: string;
  description: string;
  consequence?: string | null;
  default: unknown;
  value: unknown;
  overridden: boolean;
  /** an enum setting's fixed choices — rendered as a list */
  options?: string[] | null;
}
export function SettingsForm({ items, canEdit, onSave, onReset }: { items: SettingItem[]; canEdit: boolean; onSave: (changes: Record<string, unknown>) => Promise<unknown>; onReset: (key: string) => Promise<unknown> }) {
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const dirtyKeys = Object.keys(draft).filter((k) => JSON.stringify(draft[k]) !== JSON.stringify(items.find((i) => i.key === k)?.value));
  const dirty = dirtyKeys.length > 0;
  useReportDirty("settings-form", dirty);
  const groups = useMemo(() => {
    const g = new Map<string, SettingItem[]>();
    for (const it of items) g.set(it.group, [...(g.get(it.group) ?? []), it]);
    return [...g.entries()];
  }, [items]);
  const save = async () => {
    setSaving(true);
    try {
      const changes: Record<string, unknown> = {};
      for (const k of dirtyKeys) changes[k] = draft[k];
      await onSave(changes);
      setDraft({});
      toast.success(`${dirtyKeys.length} setting${dirtyKeys.length === 1 ? "" : "s"} saved`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div>
      {groups.map(([group, list]) => (
        <div key={group} className="card mb-5">
          <div className="card-head">
            <h3>{group}</h3>
          </div>
          <div className="card-body">
            {list.map((it) => (
              <SettingRow key={it.key} item={it} value={it.key in draft ? draft[it.key] : it.value} canEdit={canEdit} onChange={(v) => setDraft((d) => ({ ...d, [it.key]: v }))} onReset={async () => { await onReset(it.key); setDraft((d) => { const n = { ...d }; delete n[it.key]; return n; }); toast.success(`"${it.label}" reset to default`); }} />
            ))}
          </div>
        </div>
      ))}
      {dirty && (
        <div className="save-bar card">
          <span className="note">{dirtyKeys.length} unsaved change{dirtyKeys.length === 1 ? "" : "s"} — nothing applies until you save</span>
          <Button variant="ghost" onClick={() => setDraft({})} disabled={saving}>
            Discard
          </Button>
          <Button variant="primary" onClick={save} loading={saving}>
            Save settings
          </Button>
        </div>
      )}
    </div>
  );
}

function SettingRow({ item, value, canEdit, onChange, onReset }: { item: SettingItem; value: unknown; canEdit: boolean; onChange: (v: unknown) => void; onReset: () => Promise<void> }) {
  const id = useId();
  const isObj = typeof item.default === "object" && item.default !== null;
  const isNum = typeof item.default === "number";
  return (
    <div className="field" style={{ borderBottom: "1px solid var(--border)", paddingBottom: 14 }}>
      <div className="between wrap">
        <label htmlFor={id}>{item.label}</label>
        <span className="small muted flex">
          Default: <span className="mono">{isObj ? JSON.stringify(item.default) : String(item.default)}</span>
          {item.overridden && canEdit && (
            <Button size="xs" variant="ghost" onClick={onReset}>
              Reset to default
            </Button>
          )}
        </span>
      </div>
      {isObj ? (
        <div className="flex wrap mt-2">
          {Object.entries(item.default as Record<string, unknown>).map(([k, dv]) => (
            <span key={k} className="flex">
              <span className="small muted">{k}</span>
              <Input id={k === Object.keys(item.default as object)[0] ? id : undefined} small type={typeof dv === "number" ? "number" : "text"} style={{ width: 110 }} disabled={!canEdit} value={String((value as Record<string, unknown>)?.[k] ?? "")} onChange={(e) => onChange({ ...(value as Record<string, unknown>), [k]: typeof dv === "number" ? Number(e.target.value) : e.target.value })} />
            </span>
          ))}
        </div>
      ) : typeof item.default === "boolean" ? (
        <Switch checked={!!value} disabled={!canEdit} onChange={onChange} />
      ) : item.options?.length ? (
        <Select id={id} small style={{ maxWidth: 260 }} disabled={!canEdit} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
          {item.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </Select>
      ) : (
        <Input id={id} small type={isNum ? "number" : "text"} style={{ maxWidth: 260 }} disabled={!canEdit} value={String(value ?? "")} onChange={(e) => onChange(isNum ? Number(e.target.value) : e.target.value)} />
      )}
      <div className="help">{item.description}</div>
      {item.consequence && <div className="help" style={{ color: "var(--warning)" }}>{item.consequence}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* ImpactPreview — "what this would change", all / none / these         */
/* ------------------------------------------------------------------ */
export interface ImpactGroup {
  key: string;
  label: ReactNode;
  items: Array<{ id: string; label: ReactNode; sub?: ReactNode; excludedByDefault?: boolean; excludedReason?: string }>;
}
export function ImpactPreview({ groups, selected, onChange }: { groups: ImpactGroup[]; selected: Set<string>; onChange: (s: Set<string>) => void }) {
  const all = groups.flatMap((g) => g.items);
  const setAll = (on: boolean, includeExcluded = false) => onChange(new Set(on ? all.filter((i) => includeExcluded || !i.excludedByDefault).map((i) => i.id) : []));
  const toggle = (id: string) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    onChange(n);
  };
  return (
    <div>
      <div className="flex wrap mb-3">
        <Button size="xs" onClick={() => setAll(true)}>
          All (default)
        </Button>
        <Button size="xs" onClick={() => setAll(true, true)}>
          All incl. excluded
        </Button>
        <Button size="xs" onClick={() => setAll(false)}>
          None
        </Button>
        <span className="small muted right">{selected.size} of {all.length} selected</span>
      </div>
      {groups.map((g) => (
        <div className="impact-group" key={g.key}>
          <div className="ig-head">
            <span>{g.label}</span>
            <span className="small muted">{g.items.filter((i) => selected.has(i.id)).length} / {g.items.length}</span>
          </div>
          {g.items.map((it) => (
            <label key={it.id} className={`ig-row${it.excludedByDefault && !selected.has(it.id) ? " excluded" : ""}`}>
              <input type="checkbox" className="row-check" checked={selected.has(it.id)} onChange={() => toggle(it.id)} />
              <span style={{ flex: 1 }}>
                <div>{it.label}</div>
                {it.sub && <div className="small muted">{it.sub}</div>}
              </span>
              {it.excludedByDefault && <Badge tone="info" title={it.excludedReason}>excluded by default</Badge>}
            </label>
          ))}
          {g.items.length === 0 && <div className="ig-row muted">Nothing affected</div>}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* RecordHistory — audit rendered as prose                              */
/* ------------------------------------------------------------------ */
export interface HistoryItem {
  id: string;
  at: string;
  prose: string;
  actorName?: string | null;
  reason?: string | null;
}
export function RecordHistory({ items, loading, error, onRetry, hasMore, onMore }: { items: HistoryItem[]; loading?: boolean; error?: unknown; onRetry?: () => void; hasMore?: boolean; onMore?: () => void }) {
  const showSkeleton = useDelayed(!!loading && items.length === 0);
  if (error) return <EmptyState error title="Could not load history" text={errorMessage(error)} actions={onRetry && <Button onClick={onRetry}>Try again</Button>} />;
  if (loading && !items.length) return showSkeleton ? <SkeletonRows rows={4} cols={2} /> : null;
  if (!items.length) return <EmptyState icon="history" title="No history yet" text="Changes to this record will appear here in plain words." />;
  return (
    <div className="history">
      {items.map((h) => (
        <div className="history-row" key={h.id}>
          <span className="h-dot" />
          <div className="h-text">
            {h.prose}
            {h.reason && <div className="small muted">Reason: {h.reason}</div>}
          </div>
          <span className="h-when" title={formatDateTime(h.at)}>
            {relativeTime(h.at)}
          </span>
        </div>
      ))}
      {hasMore && onMore && (
        <div style={{ padding: 12, textAlign: "center" }}>
          <Button size="sm" onClick={onMore} loading={loading}>
            Show older
          </Button>
        </div>
      )}
    </div>
  );
}
